import { ErrorModal } from "@dexteel/mesf-core";
import { CircularProgress, TextField } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import { Grade } from "../model/Grade";
import { getGrades } from "../repositories/GradeRepository";

type Props = {
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  disabled?: boolean;
  highlight?: boolean;
  disableClearable?: boolean;
};

export const GradeSelector = ({
  value,
  onChange = () => {},
  onBlur = () => {},
  onFocus = () => {},
  disabled = false,
  highlight = false,
  disableClearable = false,
}: Props) => {
  const [grades, setGrades] = useState<Grade[]>([]);
  const [selectedGrade, setSelectedGrade] = useState<Grade | null>(null);
  const filteredGradesRef = useRef<Grade[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    try {
      const resp = await getGrades(false);
      if (resp.ok) {
        const rows = get(resp, "data.tables[0].rows", []) as Grade[];
        rows.sort((a, b) => a.GradeName.localeCompare(b.GradeName));
        setGrades(rows);
      } else {
        setError(resp.message);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while loading grades",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();

      const availableFilteredGrades = filteredGradesRef.current.filter(
        (grade) => grade.Enabled,
      );

      if (availableFilteredGrades.length === 1) {
        const selectedOption = availableFilteredGrades[0];
        setSelectedGrade(selectedOption);
        onChange(selectedOption.GradeId);

        inputRef.current?.blur();
      }
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (value !== null && value !== undefined) {
      const grade = grades.find((g) => g.GradeId === value);
      setSelectedGrade(grade || null);
    } else {
      setSelectedGrade(null);
    }
  }, [value, grades]);

  return (
    <>
      <Autocomplete
        id="grade-selector"
        disableClearable={disableClearable}
        clearOnEscape={!disableClearable}
        options={grades || []}
        getOptionLabel={(option) => option.GradeName}
        getOptionDisabled={(option) => !option.Enabled}
        filterOptions={(options, state) => {
          const filtered = options.filter((option) =>
            option.GradeName.toLowerCase().includes(
              state.inputValue.toLowerCase(),
            ),
          );
          filteredGradesRef.current = filtered;
          return filtered;
        }}
        disabled={disabled}
        loading={isLoading}
        value={selectedGrade}
        onChange={(event, newValue) => {
          setSelectedGrade(newValue);
          onChange(newValue?.GradeId ?? null);
        }}
        renderOption={(props, option, state) => {
          const isAvailable = option.Enabled;
          return (
            <li {...props}>
              <span
                style={{
                  fontWeight: state.selected ? "bold" : "normal",
                  opacity: isAvailable ? 1 : 0.8,
                }}
              >
                {option.GradeName}
                {!isAvailable && " (Disabled)"}
              </span>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            label={"Grades"}
            variant="outlined"
            fullWidth
            sx={
              highlight
                ? {
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(249, 248, 113, 0.35)",
                    },
                  }
                : undefined
            }
            onKeyDown={handleKeyDown}
            slotProps={{
              inputLabel: {
                sx: { fontSize: "clamp(.7rem,.8rem,1rem)" },
              },
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : (
                      params.InputProps.endAdornment
                    )}
                  </>
                ),
              },
            }}
            onBlur={onBlur}
            onFocus={onFocus}
          />
        )}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error loading Grades"
      />
    </>
  );
};
